import { useEffect, useState } from "react";
import { api } from "../../api";
import { toast } from "sonner";
import { RotateCcw, Trash2 } from "lucide-react";
import { useSortedList } from "../../components/useSortedList";
import { useConfirm } from "../../components/Confirm";

const DEPT_LABELS = {
  scouting: "Scouting", demo_submissions: "Demo Submissions", release_planning: "Release Planning",
  released_songs: "Released Songs", artist_database: "Artist Database", royalty_splits: "Royalty Splits",
  marketing: "Marketing", content_strategy: "Content Strategy", future_strategy: "Future Strategy",
  feedback: "Feedback", finance: "Finance", legal_tracker: "Legal Tracker",
};

export default function RecentlyDeleted() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const SORTABLE = [{ key: "department", label: "Department" }];
  const { sorted: sortedItems, SortBar, sortBarProps, Pagination, paginationProps } = useSortedList(items, SORTABLE);
  const confirm = useConfirm();

  const restore = (id) => {
    confirm(
      "Bring this entry back to its original department where you can continue editing it.",
      async () => {
        try { await api.deleted.restore(id); toast.success("Restored"); load(); }
        catch (e) { toast.error(String(e.message || e)); }
      },
      { title: "Restore this entry?", confirmLabel: "Yes, restore", cancelLabel: "Not now", tone: "cyan" }
    );
  };
  const purge = (id) => {
    confirm(
      "Permanently delete this entry? This action can't be undone — the record will be gone forever.",
      async () => {
        try { await api.deleted.purge(id); toast.success("Deleted permanently"); load(); }
        catch (e) { toast.error(String(e.message || e)); }
      },
      { title: "Delete forever?", confirmLabel: "Yes, delete forever" }
    );
  };

  const load = async () => {
    setLoading(true);
    try { setItems(await api.deleted.list()); } catch (e) { toast.error(String(e.message || e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="muted" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>System</div>
        <h1 style={{ fontSize: 30, margin: "4px 0 0 0" }}>Recently Deleted</h1>
        <p className="muted" style={{ marginTop: 8, fontSize: 12, maxWidth: 640 }}>
          Items sit here for 15 days, then are auto-purged. Restore anytime, or delete permanently now.
        </p>
      </div>

      <div className="panel" style={{ overflow: "hidden" }}>
        {loading ? <div style={{ padding: 20 }} className="muted">Loading…</div> :
         items.length === 0 ? <div style={{ padding: 40, textAlign: "center" }} className="muted">Nothing here.</div> :
        (
          <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid var(--border)", flexWrap: "wrap", gap: 12 }}>
            <SortBar {...sortBarProps} />
            <span className="muted" style={{ fontSize: 11 }} data-testid="entry-count">{paginationProps.total} item{paginationProps.total === 1 ? "" : "s"}</span>
          </div>
          <table>
            <thead><tr><th>Department</th><th>Summary</th><th>Deleted by</th><th>Deleted at</th><th style={{ textAlign: "right" }}></th></tr></thead>
            <tbody>
              {sortedItems.map(e => (
                <tr key={e.id} data-testid={`deleted-row-${e.id}`}>
                  <td><span className="badge">{DEPT_LABELS[e.department] || e.department}</span></td>
                  <td style={{ maxWidth: 400, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {summarize(e)}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>{e.deleted_by || "—"}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{new Date(e.deleted_at).toLocaleDateString()}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button className="btn" onClick={() => restore(e.id)} data-testid={`restore-${e.id}`} style={{ padding: "5px 10px", fontSize: 12 }}><RotateCcw size={12} /> Restore</button>{" "}
                    <button className="btn btn-danger" onClick={() => purge(e.id)} data-testid={`purge-${e.id}`} style={{ padding: "5px 9px", fontSize: 12 }}><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination {...paginationProps} />
          </>
        )}
      </div>
    </div>
  );
}

function summarize(e) {
  const d = e.data || {};
  const first = d.title || d.name || d.artist || d.song || d.campaign || d.description || d.document_type || d.song_title || "(entry)";
  return String(first);
}
